import { zodResolver } from '@hookform/resolvers/zod';
import { Action, Modal, SimpleSelect } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { Controller, useForm } from 'react-hook-form';
import { useShippingClaim } from '~/features/shippingClaimReview/ShippingClaimReviewProvider';
import { useShippingClaimUpdate } from '~/features/shippingClaimReview/hooks/useShippingClaimUpdate';
import { shippingClaimResolutionMethodName } from '~/utils/enumNameMaps';
import { useEnabledShippingResolutionMethods } from '../hooks/useShippingClaimLookupConfig';

type ShippingClaimUpdate = CrewMerchantUi.ShippingClaimUpdate;

export function EditShippingClaim({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const shippingClaim = useShippingClaim();
  const { mutateAsync: updateClaim, isPending } = useShippingClaimUpdate();
  const resolutionMethods = useEnabledShippingResolutionMethods();

  const formId = 'edit-shipping-claim-form';
  // `values` keeps the form in step with the claim after a save, `defaultValues` would not
  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(CrewMerchantUi.shippingClaimUpdateSchema),
    values: {
      // status is closed and restored from the page's More Actions, never edited here
      status: shippingClaim.status,
      resolutionMethod: shippingClaim.resolutionMethod,
    } satisfies ShippingClaimUpdate,
  });

  const closeAndReset = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      title="Edit Claim"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Save
          </Action>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          handleSubmit(async (data) => {
            await updateClaim(data);
            closeAndReset();
          })(e).catch((error: unknown) =>
            console.error('Failed to update shipping claim', error),
          );
        }}
        className="flex flex-col gap-4"
      >
        <Controller
          control={control}
          name="resolutionMethod"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Resolution Method"
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
              options={Object.values(resolutionMethods)
                .map((value) => ({
                  value,
                  label: shippingClaimResolutionMethodName[value],
                }))
                .sort((a, b) => a.label.localeCompare(b.label))}
            />
          )}
        />
      </form>
    </Modal>
  );
}
